import { ArrowRight, Building2, ShieldCheck, Award, MapPin } from "lucide-react";

export default function Hero({ bootStamp }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute inset-0 z-0 enea-paper-bg" />
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-[#a9d6e5]/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#dbeafe]/40 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-8 gap-x-2 md:gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="enea-eyebrow flex items-center gap-3 mb-7" data-testid="hero-eyebrow">
              <span className="inline-block w-8 h-px bg-[#2c7da0]" />
              <span>Un progetto Gruppo Vela</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[60px] font-display font-semibold leading-[1.05] text-[#0b2545] enea-fade-up">
              La tua casa è pronta per il mercato
              <span className="block mt-3 font-normal text-slate-700 text-3xl sm:text-4xl lg:text-5xl">
                o è destinata a restare <span className="text-[#2c7da0] font-medium">invenduta per mesi</span>?
              </span>
            </h1>

            <p className="mt-8 max-w-2xl text-base lg:text-lg text-slate-600 leading-relaxed" data-testid="hero-subtitle">
              Scopri l'esatto valore di mercato del tuo immobile e verifica se possiede i requisiti
              per accedere al protocollo <b>“Vendita Zero Vincoli”</b>.
            </p>
            <p className="mt-4 max-w-2xl text-sm text-slate-500 leading-relaxed" data-testid="hero-support">
              Il 70% delle compravendite salta a causa di mutui negati e proposte vincolate. Usa la
              diagnostica avanzata di Enea Specialist per ottenere una valutazione scientifica e scoprire
              come vendere a clienti certificati, pronti a comprare senza clausole.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="/valutazione"
                className="group inline-flex items-center gap-3 px-7 py-3.5 bg-[#0b2545] text-white hover:bg-[#13315c] transition-colors duration-200 rounded-md font-semibold text-[15px] shadow-sm"
                data-testid="hero-cta-primary"
              >
                Inizia la Diagnostica dell'Immobile
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={() => scrollTo("mcm")}
                className="inline-flex items-center gap-3 px-7 py-3.5 bg-white border border-slate-300 text-[#0b2545] hover:border-[#2c7da0] hover:text-[#2c7da0] transition-colors duration-200 rounded-md font-medium text-[15px]"
                data-testid="hero-cta-secondary"
              >
                Scopri il metodo MCM
              </button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0d9488]" /> Standard IVS / EVS</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#0d9488]" /> Perizie difendibili</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0d9488]" /> Tutta Italia</div>
            </div>
          </div>

          {/* Right card — credentials snapshot */}
          <aside className="col-span-12 lg:col-span-5" data-testid="hero-readout">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-tr from-[#a9d6e5]/40 to-[#dbeafe]/40 rounded-2xl blur-xl" />
              <div className="relative bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200 overflow-hidden">
                <div className="aspect-[4/3] w-full bg-slate-100 overflow-hidden relative">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
                    alt="Immobili in Italia"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1200&q=80"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545]/30 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5 flex items-center gap-3 text-white">
                    <Building2 className="w-5 h-5" />
                    <div>
                      <div className="text-xs font-medium uppercase tracking-[0.18em] text-white/80">Patrimonio analizzato</div>
                      <div className="text-base font-semibold">Italia · 2026</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 lg:p-7">
                  <div className="text-xs uppercase tracking-[0.18em] font-medium text-[#2c7da0] mb-4">Coordinate tecniche</div>
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-baseline justify-between border-b border-slate-100 pb-3">
                      <dt className="text-slate-500">Indice MCM Composito</dt>
                      <dd className="font-semibold text-[#0b2545]">118.4 <span className="text-[#0d9488] text-xs font-medium ml-1">+2.3%</span></dd>
                    </div>
                    <div className="flex items-baseline justify-between border-b border-slate-100 pb-3">
                      <dt className="text-slate-500">Scarto stima/rogito</dt>
                      <dd className="font-semibold text-[#0b2545]">0,8%</dd>
                    </div>
                    <div className="flex items-baseline justify-between border-b border-slate-100 pb-3">
                      <dt className="text-slate-500">Comparabili attivi</dt>
                      <dd className="font-semibold text-[#0b2545]">1.482</dd>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <dt className="text-slate-500">Aggiornamento</dt>
                      <dd className="font-medium text-slate-600">{bootStamp ? bootStamp.slice(0, 10) : "—"}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Bottom strip — 4 elegant KPIs */}
        <div className="mt-20 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
          {[
            { k: "01", l: "Anno di riferimento", v: "2026" },
            { k: "02", l: "Aree presidiate", v: "Italia" },
            { k: "03", l: "Transazioni in analisi", v: "1.480+" },
            { k: "04", l: "Aggiornamento dati", v: "Continuo" },
          ].map((it) => (
            <div key={it.k} className="bg-white p-7 lg:p-8" data-testid={`hero-kpi-${it.k}`}>
              <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#2c7da0]">{it.k}</div>
              <div className="mt-3 text-xs uppercase tracking-wider text-slate-500">{it.l}</div>
              <div className="mt-2 text-2xl lg:text-3xl font-display font-semibold text-[#0b2545] tracking-tight">{it.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
